import {Col, Divider, Form, InputNumber, Row, Select, Switch} from "../_snowpack/pkg/antd.js";
import React from "../_snowpack/pkg/react.js";
import {useTranslation} from "../_snowpack/pkg/react-i18next.js";
import {
  formatDigits,
  formatKwh,
  formatPercentage,
  formatRupiah,
  parseKwh,
  parseNumber,
  parsePercentage,
  parseRupiah
} from "../services/Formatters.js";
import {MapPicker} from "./MapPicker.js";
import {
  CALCULATOR_SETTINGS,
  INITIAL_INPUT_DATA,
  InverterPrice,
  MonthlyUsage,
  OptimizationTarget,
  powerOptions
} from "../constants.js";
import {
  FacebookOutlined,
  InfoCircleOutlined,
  LinkedinOutlined,
  ShareAltOutlined,
  TwitterOutlined
} from "../_snowpack/pkg/@ant-design/icons.js";
import {Documentation} from "../services/DocumentationService.js";
import {NumberParam, useQueryParam, withDefault} from "../_snowpack/pkg/use-query-params.js";
import {BooleanParam, createEnumParam} from "../_snowpack/pkg/serialize-query-params/lib/params.js";
import "./InputForm.css";
import * as Analytics from "../services/Analytics.js";
import {Category} from "../services/Analytics.js";
const createLink = () => {
  return `${window.location}`.replace("expertMode=1&", "");
};
const createFacebookLink = () => {
  const link = createLink();
  return `https://www.facebook.com/sharer.php?u=${encodeURI(link)}`;
};
const createTwitterLink = () => {
  const link = createLink();
  return `https://twitter.com/intent/tweet?url=${encodeURI(link)}`;
};
const createLinkedinLink = () => {
  const link = createLink();
  return `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURI(link)}`;
};
export const InputForm = (props) => {
  const {t, i18n} = useTranslation();
  const [form] = Form.useForm();
  const renderOption = (option) => {
    return /* @__PURE__ */ React.createElement(Select.Option, {
      key: option.value,
      value: option.value
    }, option.name);
  };
  const init = props.initialValue;
  const calcSettings = init.calculatorSettings;
  const plnSettings = calcSettings.plnSettings;
  const priceSettings = calcSettings.priceSettings;
  const [priorityEnabled, setPriorityEnabled] = useQueryParam("priorityEnabled", withDefault(BooleanParam, calcSettings.priorityEnabled));
  const [monthlyUsageType, setMonthlyUsageType] = useQueryParam("monthlyUsageType", withDefault(createEnumParam(Object.values(MonthlyUsage)), priceSettings.monthlyUsageType));
  const [lowTariff, setLowTariff] = useQueryParam("lowTariff", withDefault(NumberParam, plnSettings.lowTariff));
  const [highTariff, setHighTariff] = useQueryParam("highTariff", withDefault(NumberParam, plnSettings.highTariff));
  const [energyTax, setEnergyTax] = useQueryParam("energyTax", withDefault(NumberParam, plnSettings.energyTax));
  const [lowTariffThreshold, setLowTariffThreshold] = useQueryParam("lowTariffThreshold", withDefault(NumberParam, plnSettings.lowTariffThreshold));
  const [minimalMonthlyConsumptionHours, setMinimalMonthlyConsumptionHours] = useQueryParam("minimalMonthlyConsumptionHours", withDefault(NumberParam, plnSettings.minimalMonthlyConsumptionHours));
  const [minimalMonthlyConsumptionPrice, setMinimalMonthlyConsumptionPrice] = useQueryParam("minimalMonthlyConsumptionPrice", withDefault(NumberParam, plnSettings.minimalMonthlyConsumptionPrice));
  const [pricePerPanel, setPricePerPanel] = useQueryParam("pricePerPanel", withDefault(NumberParam, priceSettings.pricePerPanel));
  const [electricityPriceInflationRate, setElectricityPriceInflationRate] = useQueryParam("electricityPriceInflationRate", withDefault(NumberParam, priceSettings.electricityPriceInflationRate));
  const [capacityLossRate, setCapacityLossRate] = useQueryParam("capacityLossRate", withDefault(NumberParam, priceSettings.capacityLossRate));
  const [kiloWattPeakPerPanel, setKiloWattPeakPerPanel] = useQueryParam("kiloWattPeakPerPanel", withDefault(NumberParam, calcSettings.kiloWattPeakPerPanel));
  const [areaPerPanel, setAreaPerPanel] = useQueryParam("areaPerPanel", withDefault(NumberParam, calcSettings.areaPerPanel));
  const [lossFromInverter, setLossFromInverter] = useQueryParam("lossFromInverter", withDefault(NumberParam, calcSettings.lossFromInverter));
  const [inverterPrice, setInverterPrice] = useQueryParam("inverterPrice", withDefault(createEnumParam(Object.values(InverterPrice)), priceSettings.inverterPrice));
  const [inverterLifetimeInYears, setInverterLifetimeInYears] = useQueryParam("inverterLifetimeInYears", withDefault(NumberParam, calcSettings.inverterLifetimeInYears));
  const [priceOfInverterFactor, setPriceOfInverterFactor] = useQueryParam("priceOfInverterFactor", withDefault(NumberParam, priceSettings.priceOfInverterFactor));
  const [priceOfInverterAbsolute, setPriceOfInverterAbsolute] = useQueryParam("priceOfInverterAbsolute", withDefault(NumberParam, priceSettings.priceOfInverterAbsolute));
  const [installationCosts, setInstallationCosts] = useQueryParam("installationCosts", withDefault(NumberParam, priceSettings.installationCosts));
  const [connectionPower, setConnectionPower] = useQueryParam("cp", withDefault(NumberParam, init.connectionPower));
  const [monthlyCostEstimateInRupiah, setMonthlyCostEstimateInRupiah] = useQueryParam("me", withDefault(NumberParam, init.monthlyCostEstimateInRupiah));
  return /* @__PURE__ */ React.createElement(Form, {
    form,
    layout: "vertical",
    name: "calculator",
    onFieldsChange: (changedFields) => {
      const firstFields = changedFields[0];
      const name = JSON.stringify(firstFields.name).replace('["', "").replace('"]', "");
      Analytics.event(Category.Form, name, JSON.stringify(firstFields.value));
      const monthlyBill = form.getFieldValue("monthlyBill");
      const monthlyUsageInKwh = form.getFieldValue("monthlyUsageInKwh");
      const location = form.getFieldValue("location");
      const pvOut = location.info?.pvout;
      const targetValue = form.getFieldValue("optimizationTarget");
      const optimizationTarget = targetValue === void 0 || targetValue ? OptimizationTarget.Money : OptimizationTarget.Green;
      const calculatorSettings = {
        plnSettings: {
          lowTariff,
          highTariff,
          lowTariffThreshold,
          energyTax,
          minimalMonthlyConsumptionHours,
          minimalMonthlyConsumptionPrice
        },
        priceSettings: {
          pricePerPanel,
          electricityPriceInflationRate,
          priceOfInverterFactor,
          priceOfInverterAbsolute,
          installationCosts,
          capacityLossRate,
          inverterPrice,
          monthlyUsageType
        },
        kiloWattPeakPerPanel,
        areaPerPanel,
        lossFromInverter,
        inverterLifetimeInYears,
        kiloWattHourPerYearPerKWp: CALCULATOR_SETTINGS.kiloWattHourPerYearPerKWp,
        priorityEnabled
      };
      props.onChange({
        monthlyCostEstimateInRupiah: monthlyBill,
        monthlyUsageInKwh,
        connectionPower,
        pvOut,
        optimizationTarget,
        calculatorSettings
      });
    }
  }, /* @__PURE__ */ React.createElement(Row, {
    gutter: 16
  }), /* @__PURE__ */ React.createElement(Form.Item, {
    name: "location",
    label: /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("span", {
      className: "numberCircle"
    }, /* @__PURE__ */ React.createElement("span", null, "1")), " ", t("inputForm.location"))),
    initialValue: INITIAL_INPUT_DATA.location,
    tooltip: {
      trigger: "click",
      overlay: "",
      icon: /* @__PURE__ */ React.createElement(InfoCircleOutlined, {
        onClickCapture: () => props.onOpenDocumentation(Documentation.Location, t("inputForm.location"))
      })
    }
  }, /* @__PURE__ */ React.createElement(MapPicker, {
    mobile: props.mobile
  })), /* @__PURE__ */ React.createElement(Row, {
    gutter: 16
  }, /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: priorityEnabled ? 10 : 12
  }, monthlyUsageType === MonthlyUsage.Rupiah ? /* @__PURE__ */ React.createElement(Form.Item, {
    name: "monthlyBill",
    label: /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("span", {
      className: "numberCircle"
    }, /* @__PURE__ */ React.createElement("span", null, "2")), " ", t("inputForm.monthlyBill"))),
    initialValue: monthlyCostEstimateInRupiah,
    tooltip: {
      trigger: "click",
      overlay: "",
      icon: /* @__PURE__ */ React.createElement(InfoCircleOutlined, {
        onClickCapture: () => props.onOpenDocumentation(Documentation.MonthlyBill, t("inputForm.monthlyBill"))
      })
    }
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    formatter: formatRupiah,
    parser: parseRupiah,
    step: 1e5,
    inputMode: "numeric",
    onChange: setMonthlyCostEstimateInRupiah
  })) : /* @__PURE__ */ React.createElement(Form.Item, {
    name: "monthlyUsageInKwh",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.monthlyUsage")),
    initialValue: init.monthlyUsageInKwh
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    formatter: (value) => formatKwh(value),
    parser: (displayValue) => parseKwh(displayValue),
    step: 10
  }))), /* @__PURE__ */ React.createElement(Col, {
    xs: priorityEnabled ? 15 : 24,
    sm: priorityEnabled ? 9 : 12
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "connectionPower",
    label: /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("span", {
      className: "numberCircle"
    }, /* @__PURE__ */ React.createElement("span", null, "3")), " ", t("inputForm.connectionPower"))),
    initialValue: connectionPower,
    tooltip: {
      trigger: "click",
      overlay: "",
      icon: /* @__PURE__ */ React.createElement(InfoCircleOutlined, {
        onClickCapture: () => props.onOpenDocumentation(Documentation.ConnectionPower, t("inputForm.connectionPower"))
      })
    }
  }, /* @__PURE__ */ React.createElement(Select, {
    style: {width: "100%"},
    defaultValue: connectionPower,
    onChange: (val, _) => setConnectionPower(val)
  }, powerOptions.map(renderOption)))), priorityEnabled && /* @__PURE__ */ React.createElement(Col, {
    xs: 9,
    sm: 5
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "optimizationTarget",
    valuePropName: "checked",
    initialValue: true,
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.priority")),
    tooltip: {
      overlay: "",
      trigger: "click",
      icon: /* @__PURE__ */ React.createElement(InfoCircleOutlined, {
        onClickCapture: () => props.onOpenDocumentation(Documentation.Priority, t("inputForm.priority"))
      })
    }
  }, /* @__PURE__ */ React.createElement(Switch, {
    className: "prioritySwitch",
    checkedChildren: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.priorityMoney")),
    unCheckedChildren: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.priorityEarth")),
    defaultChecked: true
  })))), props.expertMode && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Divider, {
    orientation: "left"
  }, /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.title.plnSettings"), "  ", /* @__PURE__ */ React.createElement(InfoCircleOutlined, {
    onClickCapture: () => props.onOpenDocumentation(Documentation.PlnSettings, t("inputForm.expertMode.title.plnSettings"))
  }))), /* @__PURE__ */ React.createElement(Row, {
    gutter: 16
  }, /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 8
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "lowTariff",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.lowTariff")),
    initialValue: lowTariff
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: lowTariff,
    formatter: formatRupiah,
    parser: parseRupiah,
    onChange: setLowTariff
  }))), /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 8
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "highTariff",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.highTariff")),
    initialValue: highTariff
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: highTariff,
    formatter: formatRupiah,
    parser: parseRupiah,
    onChange: setHighTariff
  }))), /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 8
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "lowTariffThreshold",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.lowTariffThreshold")),
    initialValue: lowTariffThreshold
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: lowTariffThreshold,
    onChange: setLowTariffThreshold
  })))), /* @__PURE__ */ React.createElement(Row, {
    gutter: 16
  }, /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 4
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "energyTax",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.energyTax")),
    initialValue: energyTax
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: energyTax,
    formatter: (value) => formatPercentage(value, i18n.language),
    parser: (displayValue) => parsePercentage(displayValue),
    onChange: setEnergyTax,
    step: 0.01
  }))), /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 10
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "minimalMonthlyConsumptionHours",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.minimalMonthlyConsumptionHours")),
    initialValue: minimalMonthlyConsumptionHours
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: minimalMonthlyConsumptionHours,
    onChange: setMinimalMonthlyConsumptionHours
  }))), /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 10
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "minimalMonthlyConsumptionPrice",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.minimalMonthlyConsumptionPrice")),
    initialValue: minimalMonthlyConsumptionPrice
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: minimalMonthlyConsumptionPrice,
    onChange: setMinimalMonthlyConsumptionPrice
  })))), /* @__PURE__ */ React.createElement(Divider, {
    orientation: "left"
  }, /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.title.systemSettings"))), /* @__PURE__ */ React.createElement(Row, {
    gutter: 16
  }, /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 6
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "pricePerPanel",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.pricePerPanel")),
    initialValue: pricePerPanel
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: pricePerPanel,
    formatter: formatRupiah,
    parser: parseRupiah,
    step: 1e5,
    onChange: setPricePerPanel
  }))), /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 8
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "electricityPriceInflationRate",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.electricityPriceInflationRate")),
    initialValue: electricityPriceInflationRate
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: electricityPriceInflationRate,
    formatter: (value) => formatPercentage(value, i18n.language, 2),
    parser: (displayValue) => parsePercentage(displayValue),
    step: 0.01,
    onChange: setElectricityPriceInflationRate
  })))), /* @__PURE__ */ React.createElement(Row, {
    gutter: 16
  }, /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 6
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "kiloWattPeakPerPanel",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.kiloWattPeakPerPanel")),
    initialValue: kiloWattPeakPerPanel
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: kiloWattPeakPerPanel,
    formatter: (value) => formatDigits(value, 3, i18n.language),
    parser: (displayValue) => parseNumber(displayValue),
    onChange: setKiloWattPeakPerPanel,
    step: 0.01
  }))), /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 6
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "areaPerPanel",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.areaPerPanel")),
    initialValue: areaPerPanel
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: areaPerPanel,
    formatter: (value) => formatDigits(value, 2, i18n.language),
    parser: (displayValue) => parseNumber(displayValue),
    step: 0.1,
    onChange: setAreaPerPanel
  }))), /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 6
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "lossFromInverter",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.lossFromInverter")),
    initialValue: lossFromInverter
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: lossFromInverter,
    formatter: (value) => formatDigits(value, 4, i18n.language),
    parser: (displayValue) => parseNumber(displayValue),
    step: 0.1,
    onChange: setLossFromInverter
  }))), /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 6
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "capacityLossRate",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.capacityLossRate")),
    initialValue: capacityLossRate
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: capacityLossRate,
    formatter: (value) => formatPercentage(value, i18n.language, 2),
    parser: (displayValue) => parsePercentage(displayValue),
    step: 1e-3,
    onChange: setCapacityLossRate
  })))), /* @__PURE__ */ React.createElement(Row, {
    gutter: 16
  }, /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 4
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "inverterPrice",
    valuePropName: "checked",
    initialValue: inverterPrice === InverterPrice.Relative,
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.inverterPrice"))
  }, /* @__PURE__ */ React.createElement(Switch, {
    checkedChildren: InverterPrice.Relative,
    unCheckedChildren: InverterPrice.Absolute,
    defaultChecked: inverterPrice === InverterPrice.Relative,
    onChange: (newValue) => setInverterPrice(newValue ? InverterPrice.Relative : InverterPrice.Absolute)
  }))), inverterPrice === InverterPrice.Absolute ? /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 6
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "priceOfInverterAbsolute",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.priceOfInverterAbsolute")),
    initialValue: priceOfInverterAbsolute
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: priceOfInverterAbsolute,
    formatter: formatRupiah,
    parser: parseRupiah,
    step: 1e5,
    onChange: setPriceOfInverterAbsolute
  }))) : /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 8
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "priceOfInverterFactor",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.priceOfInverterFactor")),
    initialValue: priceOfInverterFactor
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: priceOfInverterFactor,
    formatter: (value) => formatPercentage(value, i18n.language),
    parser: (displayValue) => parsePercentage(displayValue),
    step: 0.01,
    onChange: setPriceOfInverterFactor
  }))), /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 6
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "installationCosts",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.installationCosts")),
    initialValue: installationCosts
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: installationCosts,
    formatter: formatRupiah,
    parser: parseRupiah,
    step: 1e5,
    onChange: setInstallationCosts
  }))), /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 6
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "inverterLifetime",
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.inverterLifeTime")),
    initialValue: inverterLifetimeInYears
  }, /* @__PURE__ */ React.createElement(InputNumber, {
    style: {width: "100%", textAlign: "right"},
    autoComplete: "off",
    defaultValue: inverterLifetimeInYears,
    formatter: (value) => formatDigits(value, 1, i18n.language),
    parser: (displayValue) => parseNumber(displayValue),
    step: 1,
    onChange: setInverterLifetimeInYears
  })))), /* @__PURE__ */ React.createElement(Divider, {
    orientation: "left"
  }, /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.title.appSettings"))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 8,
    style: {fontSize: 16}
  }, "Share settings", /* @__PURE__ */ React.createElement("br", null), " ", /* @__PURE__ */ React.createElement("a", {
    href: createLink(),
    target: "_blank",
    rel: "noreferrer"
  }, /* @__PURE__ */ React.createElement(ShareAltOutlined, null)), " ", /* @__PURE__ */ React.createElement("a", {
    href: createFacebookLink(),
    target: "_blank",
    rel: "noreferrer"
  }, /* @__PURE__ */ React.createElement(FacebookOutlined, null)), " ", /* @__PURE__ */ React.createElement("a", {
    href: createTwitterLink(),
    target: "_blank",
    rel: "noreferrer"
  }, /* @__PURE__ */ React.createElement(TwitterOutlined, null)), " ", /* @__PURE__ */ React.createElement("a", {
    href: createLinkedinLink(),
    target: "_blank",
    rel: "noreferrer"
  }, /* @__PURE__ */ React.createElement(LinkedinOutlined, null))), /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 8
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "priorityEnabled",
    valuePropName: "checked",
    initialValue: priorityEnabled,
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.priorityEnabled"))
  }, /* @__PURE__ */ React.createElement(Switch, {
    defaultChecked: priorityEnabled,
    onChange: (newValue) => setPriorityEnabled(newValue)
  }))), /* @__PURE__ */ React.createElement(Col, {
    xs: 24,
    sm: 8
  }, /* @__PURE__ */ React.createElement(Form.Item, {
    name: "usageType",
    valuePropName: "checked",
    initialValue: monthlyUsageType === MonthlyUsage.Rupiah,
    label: /* @__PURE__ */ React.createElement(React.Fragment, null, t("inputForm.expertMode.usageType"))
  }, /* @__PURE__ */ React.createElement(Switch, {
    checkedChildren: MonthlyUsage.Rupiah,
    unCheckedChildren: MonthlyUsage.KWh,
    defaultChecked: monthlyUsageType === MonthlyUsage.Rupiah,
    onChange: (newValue) => setMonthlyUsageType(newValue ? MonthlyUsage.Rupiah : MonthlyUsage.KWh)
  }))))));
};
